import { Loader2, Pencil, RotateCw, Send, UserMinus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import * as React from "react";
import { relativeTime, scheduleKey } from "./agent-roster";
import { agentAsWorkflow, agentRunAsWorkflowRun } from "./agent-as-workflow";
import { WorkflowCanvas, type CanvasSelection } from "./workflow-canvas";
import { WorkflowInspector } from "./workflow-inspector";
import { useT } from "@/lib/i18n";
import type { Agent, AgentRun } from "@/lib/api/types";
import { actionTakenLabel, grantLabel } from "./action-label";

/**
 * Ce que l'agent a vu.
 *
 * Un fil chronologique et non un tableau : on lit ca comme on lit un carnet,
 * du plus recent vers le passe, et la valeur est dans la phrase, pas dans une
 * colonne. Les heures calmes restent affichees, en gris et sur une ligne : une
 * rangee d'heures sans rien est la seule facon de savoir qu'il etait la, et
 * les masquer donnerait une page vide pour un agent qui travaille tres bien.
 *
 * Ce qu'il a fait est affiche a partir de la liste d'actions relevee par la
 * plateforme, jamais depuis le rapport. Le rapport est du texte produit par un
 * modele, et c'est la seule partie de cette page qui peut se tromper avec
 * aplomb.
 *
 * Au-dessus du carnet, l'agent est dessine comme ce qu'il est : un workflow
 * d'un seul pas, sur le meme canvas et avec le meme inspecteur que les
 * workflows. Sa consigne se lit en ouvrant le pas, son dernier passage s'y
 * voit avec sa sortie et ce que la plateforme l'a vu faire.
 */

interface AgentJournalProps {
  agent: Agent;
  runs: AgentRun[];
  loading: boolean;
  running: boolean;
  onRun: () => void;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onDismiss: () => void;
  onAsk: (message: string) => void;
  asking: boolean;
}

export function AgentJournal({
  agent,
  runs,
  loading,
  running,
  onRun,
  onToggle,
  onEdit,
  onDismiss,
  onAsk,
  asking,
}: AgentJournalProps) {
  const t = useT();
  const asWorkflow = React.useMemo(() => agentAsWorkflow(agent), [agent]);
  const latest = React.useMemo(() => agentRunAsWorkflowRun(runs[0]), [runs]);
  // Le pas est ouvert d'office : c'est la qu'est la consigne, et un agent
  // n'en a qu'un. Un clic sur le fond le referme, un autre le rouvre.
  const [picked, setPicked] = React.useState<CanvasSelection>(0);
  React.useEffect(() => setPicked(0), [agent.id]);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-sm font-semibold">
            {t("agents.whatTheySaw", { name: agent.name })}
          </h2>
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-placeholder">
            <span>{t(scheduleKey(agent.schedule))}</span>
            <span aria-hidden>·</span>
            {agent.actions.length > 0 ? (
              agent.actions.map((action) => (
                <Badge key={action} tone="warning">
                  {grantLabel(action, t)}
                </Badge>
              ))
            ) : (
              <span>{t("agents.readsOnly")}</span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch checked={agent.enabled} onCheckedChange={onToggle} />
            {agent.enabled ? t("agents.atWork") : t("agents.paused")}
          </label>
          <Button
            variant="secondary"
            size="sm"
            onClick={onRun}
            disabled={running}
          >
            {running ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <RotateCw className="size-4" aria-hidden />
            )}
            {t("agents.runNow")}
          </Button>
          {/* Modifier et renvoyer vivent ici et pas sur la carte : la rangee se
              parcourt d'un regard, et deux boutons par collegue la
              transformeraient en tableau de bord. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            title={t("common.edit")}
          >
            <Pencil className="size-4" aria-hidden />
            <span className="sr-only">{t("common.edit")}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            title={t("agents.dismiss")}
          >
            <UserMinus className="size-4" aria-hidden />
            <span className="sr-only">{t("agents.dismiss")}</span>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_300px]">
        <WorkflowCanvas
          workflow={asWorkflow}
          latest={latest}
          selectedIndex={picked}
          onSelectStep={setPicked}
        />
        <WorkflowInspector
          workflow={asWorkflow}
          latest={latest}
          index={picked}
          deciding={false}
          onDecide={() => undefined}
        />
      </div>

      <AskBox name={agent.name} onAsk={onAsk} pending={asking} />

      {loading && runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("agents.loadingRuns")}
        </p>
      ) : runs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
          {t("agents.neverRan")}
        </p>
      ) : (
        <ol className="relative space-y-0 border-l border-border pl-5">
          {runs.map((run) => (
            <RunEntry key={run.id} run={run} />
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * Lui parler.
 *
 * Sous les boutons et au-dessus du carnet, parce que c'est la reponse a ce
 * qu'on vient de lire : la premiere question qu'on a sur un rapport est une
 * relance, et elle n'avait nulle part ou aller.
 *
 * Ce qu'on demande part dans le meme carnet que ses passages programmes. Une
 * conversation tenue a cote serait une seconde histoire, et la premiere
 * question qu'on pose sur une action est ce qui l'a declenchee.
 */
function AskBox({
  name,
  onAsk,
  pending,
}: {
  name: string;
  onAsk: (message: string) => void;
  pending: boolean;
}) {
  const t = useT();
  const [message, setMessage] = React.useState("");
  const send = () => {
    const asked = message.trim();
    if (!asked || pending) return;
    onAsk(asked);
    setMessage("");
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") send();
        }}
        placeholder={t("agents.askPlaceholder").replace("{name}", name)}
        disabled={pending}
      />
      <Button
        variant="secondary"
        onClick={send}
        disabled={pending || message.trim().length === 0}
      >
        {pending ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Send className="size-4" aria-hidden />
        )}
        {t("agents.ask")}
      </Button>
    </div>
  );
}

function RunEntry({ run }: { run: AgentRun }) {
  const t = useT();
  const when = relativeTime(run.startedAt, t);

  // Trois sortes de lignes, et elles ne se ressemblent pas : une panne se
  // remarque, un rapport se lit, une heure calme se survole.
  if (run.error) {
    return (
      <li className="relative py-3">
        <Dot tone="danger" />
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs text-placeholder tabular-nums">{when}</span>
          <Badge tone="danger">{t("agents.failed")}</Badge>
        </div>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          {run.error}
        </p>
      </li>
    );
  }

  if (run.quiet) {
    return (
      <li className="relative py-1.5">
        <Dot tone="muted" />
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-xs text-placeholder tabular-nums">{when}</span>
          <span className="text-xs text-placeholder">
            {t("agents.nothingToReport")}
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className="relative py-3">
      <Dot tone="brand" />
      <span className="text-xs text-placeholder tabular-nums">{when}</span>
      {/* La question au-dessus de la reponse, dans le meme fil : on relit un
          echange, pas un rapport sorti de nulle part. */}
      {run.question ? (
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">
            {t("agents.youAsked")}
          </span>{" "}
          {run.question}
        </p>
      ) : null}
      <p className="mt-1 text-sm leading-relaxed whitespace-pre-line">
        {run.report}
      </p>
      {run.actions.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {run.actions.map((action, index) => (
            <Badge key={`${action}-${index}`} tone="success">
              {actionTakenLabel(action, t)}
            </Badge>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function Dot({ tone }: { tone: "brand" | "danger" | "muted" }) {
  const color =
    tone === "danger"
      ? "bg-[var(--noryx-danger)]"
      : tone === "brand"
        ? "bg-brand"
        : "bg-border-strong";
  return (
    <span
      aria-hidden
      className={`absolute -left-[1.4rem] top-[1.15rem] size-2 rounded-full ring-2 ring-[var(--noryx-background)] ${color}`}
    />
  );
}
